import { inject, Injectable, signal, computed } from '@angular/core';
import { Params } from '@angular/router';
import {
  ContextModalParams,
  ContextModalType
} from '@portal/app/dashboard/context-modal/context-model.types';
import { environment } from '@portal/environments/environment';
import { DataResponse } from '@portal/app/shared/types';
import {
  DateTimeService,
  DayJsDateFormat
} from '@portal/app/shared/services/date-time.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SelectionService } from '@portal/app/shared/services/selection.service';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';

export const DATE_FORMAT = 'MMM DD, YYYY';

const COMMON_MODAL_PARAMS = [
  'dateStart',
  'dateStop',
  'previousDateStart',
  'previousDateStop',
  'conversionType'
] as const;

export const CHANNEL_MODAL_PARAMS = ['channel', ...COMMON_MODAL_PARAMS];
export const TACTIC_MODAL_PARAMS = ['tactic', ...CHANNEL_MODAL_PARAMS];

@Injectable()
export class ModalService {
  private selection = inject(SelectionService);
  private dateTimeService = inject(DateTimeService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  public static baseUriV1 = `${environment.apiDomain}/api/v1/bff/xcd/context-menu`;

  private returnCurveViewTypeSignal = signal<'salesI' | 'ordersI'>('ordersI');
  private comparePreviousPeriodSignal = signal<string>('');
  private compareCurrentPeriodSignal = signal<string>('');
  private modalParamsSignal = signal<ContextModalParams>(
    {} as ContextModalParams
  );

  private currentRowSignal = signal<ContextModalParams>(
    {} as ContextModalParams
  );

  private queryParamSignal = toSignal(this.route.queryParams);

  public modalParams$ = toObservable(this.modalParams);

  readonly conversionType = computed(
    () => (this.modalParamsSignal().conversionType as string) || ''
  );

  readonly modalType = computed(() =>
    this.modalParamsSignal().tactic
      ? ContextModalType.tactic
      : ContextModalType.channel
  );

  get returnCurveViewType() {
    return this.returnCurveViewTypeSignal.asReadonly();
  }

  get comparePreviousPeriod() {
    return this.comparePreviousPeriodSignal.asReadonly();
  }

  get compareCurrentPeriod() {
    return this.compareCurrentPeriodSignal.asReadonly();
  }

  get modalParams() {
    return this.modalParamsSignal.asReadonly();
  }

  get currentRow() {
    return this.currentRowSignal.asReadonly();
  }

  get isSpendLevel() {
    return computed(() => {
      const params = this.queryParamSignal();
      if (!params?.isSpendLevel) {
        return false;
      }
      const decoded = this.getDecodedURIParam(params, 'isSpendLevel');
      return decoded === 'true';
    });
  }

  setCurrentRow(row: DataResponse | ContextModalParams) {
    const currentRow = {
      ...this.modalParamsSignal(),
      ...row
    } as ContextModalParams;
    this.currentRowSignal.set(currentRow);
    this.setModalParams(currentRow);
    this.updateQueryParams(currentRow);
  }

  private updateQueryParams(row: ContextModalParams): void {
    if (!row.channel) return;
    const queryParams: Params = { channel: row.channel };
    if (row.tactic) {
      queryParams.tactic = row.tactic;
    }
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge'
    });
  }

  setModalParams(params: ContextModalParams) {
    this.modalParamsSignal.set(params);
    this.compareCurrentPeriodSignal.set(
      `${dayjs(params.dateStart).format(DATE_FORMAT)} - ${dayjs(params.dateStop).format(DATE_FORMAT)}`
    );
    this.comparePreviousPeriodSignal.set(
      `${dayjs(params.previousDateStart).format(DATE_FORMAT)} - ${dayjs(params.previousDateStop).format(DATE_FORMAT)}`
    );
  }

  getParams(): Params {
    const { clientId, brandId } = this.selection.getClientIdAndBrandId();
    return { clientId, brandId };
  }

  getLast30DaysDateParams() {
    const dateParams: Params = {};
    const currentMonth = this.dateTimeService.calculatePrevious30Days();
    const previousMonth =
      this.dateTimeService.calculatePreviousPeriod(currentMonth);
    dateParams.dateStart = currentMonth.startDate.format(
      DayJsDateFormat.fullDate
    );
    dateParams.dateStop = currentMonth.endDate.format(DayJsDateFormat.fullDate);
    dateParams.previousDateStart = previousMonth.startDate.format(
      DayJsDateFormat.fullDate
    );
    dateParams.previousDateStop = previousMonth.endDate.format(
      DayJsDateFormat.fullDate
    );
    return dateParams;
  }

  buildCloseParams(keys: readonly string[]): Record<string, null> {
    return ['modal', ...keys, 'isSpendLevel'].reduce(
      (acc, key) => {
        acc[key] = null;
        return acc;
      },
      {} as Record<string, null>
    );
  }

  getDecodedURIParam(params: Params, metric: string) {
    return params[metric] ? decodeURIComponent(params[metric]) : '';
  }

  buildModalParams(
    params: Params,
    keys: readonly string[]
  ): ContextModalParams {
    const decoded = keys.reduce(
      (acc, key) => {
        acc[key] = this.getDecodedURIParam(params, key);
        return acc;
      },
      {} as Record<string, string>
    );

    return {
      ...this.modalParams(),
      ...decoded
    } as ContextModalParams;
  }
}
